import React from 'react';
import { Brain, Lightbulb, Heart, MessageSquare, Sparkles, Feather, Leaf, ClipboardList, Sun, Moon } from 'lucide-react';
import { JournalTemplate } from '../../types';

// Separate templates into shadow work and light work
export const SHADOW_WORK_TEMPLATES: JournalTemplate[] = [
  {
    id: 'fourth-step',
    title: '4th Step Inventory',
    description: 'Conduct a fearless moral inventory',
    icon: <ClipboardList className="h-5 w-5" />,
    category: 'shadow',
    sections: [
      {
        title: 'Subject',
        key: 'subject',
        type: 'text',
        placeholder: 'What area of life are you examining? (e.g., relationships, work, family)'
      },
      {
        title: 'Resentments',
        key: 'resentments',
        type: 'list',
        placeholder: 'Who or what am I resentful towards? Be specific.'
      },
      {
        title: 'The Cause',
        key: 'cause',
        type: 'text',
        placeholder: 'What specifically happened? State the facts without justification.'
      },
      {
        title: 'Affects My',
        key: 'affects',
        type: 'list',
        placeholder: 'How was I affected? (e.g., self-esteem, security, ambitions, relationships)'
      },
      {
        title: 'My Part',
        key: 'myPart',
        type: 'text',
        placeholder: 'What was my role in this situation? Be honest about your faults and mistakes.'
      },
      {
        title: 'Character Defects (Active Protectors)',
        key: 'defects',
        type: 'list',
        placeholder: 'What character defects came into play? (e.g., pride, fear, dishonesty, selfishness)'
      },
      {
        title: 'Fears',
        key: 'fears',
        type: 'list',
        placeholder: 'What fears underlie this situation?'
      },
      {
        title: 'Ideal Response',
        key: 'ideal',
        type: 'text',
        placeholder: 'What would be the ideal way to handle this situation in the future?'
      },
      {
        title: 'Amends Needed',
        key: 'amends',
        type: 'text',
        placeholder: 'What amends need to be made? To whom?'
      },
      {
        title: 'Prayer/Meditation',
        key: 'prayer',
        type: 'text',
        placeholder: 'Write a prayer or reflection for guidance and willingness to change'
      }
    ]
  },
  {
    id: 'brain-dump',
    title: 'Shadow Exploration',
    description: 'Clear your mind of repetitive thoughts',
    icon: <Brain className="h-5 w-5" />,
    category: 'shadow',
    sections: [
      {
        title: 'Subject',
        key: 'subject',
        type: 'text',
        placeholder: 'What shadow aspect are you exploring today?'
      },
      {
        title: 'Thoughts',
        key: 'thoughts',
        type: 'text',
        placeholder: 'Let it all out without judgment...'
      }
    ]
  },
  {
    id: 'belief-tracking',
    title: 'Belief Tracking',
    description: 'Identify and transform limiting beliefs',
    icon: <Lightbulb className="h-5 w-5" />,
    category: 'shadow',
    sections: [
      {
        title: 'Subject',
        key: 'subject',
        type: 'text',
        placeholder: 'What area of life does this belief affect?'
      },
      {
        title: 'Current Limiting Belief',
        key: 'limitingBelief',
        type: 'text',
        placeholder: 'What belief is holding you back?'
      },
      {
        title: 'Evidence Against This Belief',
        key: 'evidence',
        type: 'list',
        placeholder: 'What proof do you have that this belief might not be true?'
      },
      {
        title: 'New Empowering Belief',
        key: 'newBelief',
        type: 'text',
        placeholder: 'What would be a more balanced perspective?'
      }
    ]
  },
  {
    id: 'therapy',
    title: 'Therapy Notes',
    description: 'Document therapy sessions',
    icon: <MessageSquare className="h-5 w-5" />,
    category: 'shadow',
    sections: [
      {
        title: 'Subject',
        key: 'subject',
        type: 'text',
        placeholder: 'What was the main focus of today\'s session?'
      },
      {
        title: 'Key Topics Discussed',
        key: 'topics',
        type: 'list',
        placeholder: 'What topics did you explore?'
      },
      {
        title: 'Insights & Realizations',
        key: 'insights',
        type: 'text',
        placeholder: 'What did you learn about yourself?'
      },
      {
        title: 'Action Items',
        key: 'actions',
        type: 'list',
        placeholder: 'What will you work on before next session?'
      }
    ]
  },
  {
    id: 'plant-medicine',
    title: 'Medicine Journey',
    description: 'Document and integrate medicine experiences',
    icon: <Leaf className="h-5 w-5" />,
    category: 'shadow',
    sections: [
      {
        title: 'Subject',
        key: 'subject',
        type: 'text',
        placeholder: 'What is the intention or focus of this journey?'
      },
      {
        title: 'Medicine Details',
        key: 'medicine',
        type: 'text',
        placeholder: 'What medicine did you work with? (Use code names if preferred)'
      },
      {
        title: 'Setting',
        key: 'setting',
        type: 'text',
        placeholder: 'Describe your physical environment and ceremonial container'
      },
      {
        title: 'Intention',
        key: 'intention',
        type: 'text',
        placeholder: 'What was your intention for this journey?'
      },
      {
        title: 'Physical Effects',
        key: 'physical',
        type: 'list',
        placeholder: 'What physical sensations or effects did you experience?'
      },
      {
        title: 'Emotional Journey',
        key: 'emotional',
        type: 'list',
        placeholder: 'What emotions or feelings arose?'
      },
      {
        title: 'Visions & Insights',
        key: 'visions',
        type: 'text',
        placeholder: 'Describe any visions, messages, or insights received'
      },
      {
        title: 'Shadow Material',
        key: 'shadow',
        type: 'text',
        placeholder: 'What challenging aspects or shadow material emerged?'
      },
      {
        title: 'Healing Received',
        key: 'healing',
        type: 'text',
        placeholder: 'What healing or transformation occurred?'
      },
      {
        title: 'Integration Insights',
        key: 'integration',
        type: 'list',
        placeholder: 'Key lessons to integrate into daily life'
      },
      {
        title: 'Action Steps',
        key: 'actions',
        type: 'list',
        placeholder: 'Specific actions to support integration'
      },
      {
        title: 'Support Needed',
        key: 'support',
        type: 'text',
        placeholder: 'What support do you need during integration?'
      }
    ]
  }
];

export const LIGHT_WORK_TEMPLATES: JournalTemplate[] = [
  {
    id: 'integration',
    title: 'Integration Practice',
    description: 'Document and celebrate integration milestones',
    icon: <Sun className="h-5 w-5" />,
    category: 'light',
    sections: [
      {
        title: 'Subject',
        key: 'subject',
        type: 'text',
        placeholder: 'What integration milestone are you documenting?'
      },
      {
        title: 'Parts Involved',
        key: 'partsInvolved',
        type: 'list',
        placeholder: 'Which parts were involved in this integration?'
      },
      {
        title: 'Signs of Integration',
        key: 'signs',
        type: 'list',
        placeholder: 'What evidence shows integration is happening? (e.g., new behaviors, feelings, insights)'
      },
      {
        title: 'Old Pattern',
        key: 'oldPattern',
        type: 'text',
        placeholder: 'Describe how you used to respond in similar situations'
      },
      {
        title: 'New Response',
        key: 'newResponse',
        type: 'text',
        placeholder: 'How are you responding differently now?'
      },
      {
        title: 'Inner Harmony',
        key: 'harmony',
        type: 'text',
        placeholder: 'How do your parts feel about this change? Is there any resistance?'
      },
      {
        title: 'Gratitude & Celebration',
        key: 'gratitude',
        type: 'list',
        placeholder: 'What are you grateful for in this integration process?'
      },
      {
        title: 'Next Steps',
        key: 'nextSteps',
        type: 'list',
        placeholder: 'What will help maintain and deepen this integration?'
      }
    ]
  },
  {
    id: 'gratitude',
    title: 'Gratitude List',
    description: 'Focus on appreciation and abundance',
    icon: <Heart className="h-5 w-5" />,
    category: 'light',
    sections: [
      {
        title: 'Subject',
        key: 'subject',
        type: 'text',
        placeholder: 'What area of life are you feeling grateful for?'
      },
      {
        title: 'I am grateful for...',
        key: 'gratitudeList',
        type: 'list',
        placeholder: 'Add something you\'re grateful for'
      }
    ]
  },
  {
    id: 'affirmation',
    title: 'Affirmation Journal',
    description: 'Strengthen positive beliefs',
    icon: <Sparkles className="h-5 w-5" />,
    category: 'light',
    sections: [
      {
        title: 'Subject',
        key: 'subject',
        type: 'text',
        placeholder: 'What aspect of yourself are you affirming today?'
      },
      {
        title: 'Today\'s Affirmations',
        key: 'affirmations',
        type: 'list',
        placeholder: 'Write an affirmation'
      },
      {
        title: 'How These Affirmations Make Me Feel',
        key: 'feelings',
        type: 'text',
        placeholder: 'Describe your emotional response...'
      }
    ]
  },
  {
    id: 'manifestation',
    title: 'Future Self',
    description: 'Connect with your future healed self',
    icon: <Feather className="h-5 w-5" />,
    category: 'light',
    sections: [
      {
        title: 'Subject',
        key: 'subject',
        type: 'text',
        placeholder: 'What future version of yourself are you connecting with?'
      },
      {
        title: 'Letter from My Future Self',
        key: 'letter',
        type: 'text',
        placeholder: 'Dear present me...'
      },
      {
        title: 'Action Steps',
        key: 'actions',
        type: 'list',
        placeholder: 'What steps will help you become this version of yourself?'
      }
    ]
  },
  {
    id: 'meditation',
    title: 'Post-Meditation',
    description: 'Capture insights after meditation',
    icon: <Sparkles className="h-5 w-5" />,
    category: 'light',
    sections: [
      {
        title: 'Subject',
        key: 'subject',
        type: 'text',
        placeholder: 'What type of meditation did you practice?'
      },
      {
        title: 'Physical Sensations',
        key: 'sensations',
        type: 'text',
        placeholder: 'What did you feel in your body?'
      },
      {
        title: 'Emotions That Arose',
        key: 'emotions',
        type: 'list',
        placeholder: 'What emotions came up?'
      },
      {
        title: 'Insights Received',
        key: 'insights',
        type: 'text',
        placeholder: 'What insights or messages did you receive?'
      }
    ]
  }
];

// Combine all templates for backward compatibility
export const JOURNAL_TEMPLATES = [...SHADOW_WORK_TEMPLATES, ...LIGHT_WORK_TEMPLATES];